import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import { IoMdClose } from 'react-icons/io';
import { HiOutlineSpeakerphone } from 'react-icons/hi';
import { MdOutlineOpenInNew } from 'react-icons/md';
import { getAnnouncementPdf, trackAnnouncementPdfViewed } from 'interfaces/autoOrder';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

// How many pages are mounted (rendered to canvas) at once. A one-page flyer
// never hits this, but nothing stops an admin from uploading a much longer
// PDF - without a cap, every page would render up front, which can stall or
// exhaust memory on lower-end phones.
const PAGES_PER_CHUNK = 3;

const AnnounceModal = (props: {
    isOpen: any;
    onClose: any;
    description: any;
    pdfUrl?: string | null;
}) => {
    const { isOpen, onClose, description, pdfUrl } = props;
    const pdfContainerRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
    const [pdfError, setPdfError] = useState(false);
    const [numPages, setNumPages] = useState(0);
    const [visiblePages, setVisiblePages] = useState(PAGES_PER_CHUNK);
    const [pageWidth, setPageWidth] = useState(320);
    const hasTrackedViewRef = useRef(false);

    // "Seen" means the customer actually engaged with the PDF (scrolled the
    // pop-up while it was attached, or opened it in a new tab) - not just
    // that the pop-up auto-opened, which happens for everyone regardless of
    // whether they ever look at the document. Fires once per visit.
    const trackViewed = () => {
        if (hasTrackedViewRef.current || !pdfUrl) return;
        hasTrackedViewRef.current = true;
        trackAnnouncementPdfViewed();
    };

    // Without this, scrolling the PDF iframe on mobile "chains" past its own
    // scroll boundary into the B2B shop page behind the pop-up (the shop
    // scrolls instead of/behind the PDF) because nothing stops the body from
    // scrolling while this non-Chakra modal is open.
    useEffect(() => {
        if (!isOpen) return;
        const { body, documentElement: html } = document;
        const prevBodyOverflow = body.style.overflow;
        const prevHtmlOverflow = html.style.overflow;
        body.style.overflow = 'hidden';
        html.style.overflow = 'hidden';
        return () => {
            body.style.overflow = prevBodyOverflow;
            html.style.overflow = prevHtmlOverflow;
        };
    }, [isOpen]);

    // Fetch the PDF as a blob through our own API rather than letting the
    // browser hit the R2 signed URL directly: react-pdf needs to download the
    // bytes itself to render them on canvas, which would otherwise depend on
    // R2's CORS policy being open. Rendering on canvas (instead of an
    // <iframe>) is also what makes the document scrollable at all on mobile -
    // nested native PDF viewers inside iframes don't reliably forward touch
    // scroll on iOS/Android.
    useEffect(() => {
        if (!pdfUrl || pdfBlobUrl) return;
        let objectUrl: string | null = null;
        (async () => {
            const blob = await getAnnouncementPdf();
            if (!blob) {
                setPdfError(true);
                return;
            }
            objectUrl = URL.createObjectURL(blob);
            setPdfBlobUrl(objectUrl);
        })();
        return () => {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdfUrl]);

    useEffect(() => {
        const node = pdfContainerRef.current;
        if (!node) return;
        const updateWidth = () => setPageWidth(node.clientWidth);
        updateWidth();
        const observer = new ResizeObserver(updateWidth);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    // Mount additional pages a few at a time as the sentinel below the last
    // rendered page approaches the viewport, instead of mounting all of them
    // immediately.
    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || visiblePages >= numPages) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    setVisiblePages((n) => Math.min(n + PAGES_PER_CHUNK, numPages));
                }
            },
            { rootMargin: '300px' },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [visiblePages, numPages]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[1003] flex items-center justify-center p-4">
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            />

            {/* Modal */}
            <div
                className={`relative z-[1004] w-full animate-[slideDown_0.3s_ease-out] ${
                    pdfUrl ? 'max-w-2xl' : 'max-w-lg'
                }`}
            >
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
                    {/* Header avec icône */}
                    <div className="relative shrink-0 bg-gradient-to-br from-blue-600 to-blue-700 p-6 pb-12">
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 p-2 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-full transition-all hover:scale-110"
                        >
                            <IoMdClose size={20} className="text-white" />
                        </button>

                        <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                                <HiOutlineSpeakerphone size={24} className="text-white" />
                            </div>
                            <h1 className="text-2xl font-bold text-white">
                                Annonce importante
                            </h1>
                        </div>
                    </div>

                    {/* Contenu */}
                    <div
                        className="px-6 pb-6 -mt-8 relative z-10 flex-1 overflow-y-auto overscroll-y-contain"
                        onScroll={trackViewed}
                    >
                        {description && (
                            <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
                                <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                                    {description}
                                </p>
                            </div>
                        )}

                        {pdfUrl && (
                            <div
                                ref={pdfContainerRef}
                                className="bg-white rounded-2xl shadow-lg p-3 mb-6"
                            >
                                {pdfError ? (
                                    <p className="py-6 text-center text-xs text-gray-400">
                                        Impossible de charger l'aperçu du document.
                                    </p>
                                ) : !pdfBlobUrl ? (
                                    <p className="py-6 text-center text-xs text-gray-400">
                                        Chargement du document...
                                    </p>
                                ) : (
                                    <Document
                                        file={pdfBlobUrl}
                                        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                                        onLoadError={() => setPdfError(true)}
                                    >
                                        {Array.from(
                                            { length: Math.min(visiblePages, numPages) },
                                            (_, i) => (
                                                <Page
                                                    key={i}
                                                    pageNumber={i + 1}
                                                    width={pageWidth}
                                                    className="mb-2 [&:last-child]:mb-0"
                                                />
                                            ),
                                        )}
                                        {visiblePages < numPages && (
                                            <div ref={sentinelRef} className="h-1" />
                                        )}
                                    </Document>
                                )}
                                <a
                                    href={pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={trackViewed}
                                    className="mt-2 flex items-center justify-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700"
                                >
                                    <MdOutlineOpenInNew size={14} />
                                    Le document ne s'affiche pas ? Ouvrir dans un nouvel onglet
                                </a>
                            </div>
                        )}

                        {/* Bouton d'action */}
                        <button
                            onClick={onClose}
                            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold shadow-lg hover:shadow-xl transition-all hover:scale-[1.02]"
                        >
                            Compris !
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes slideDown {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </div>
    );
};

export default AnnounceModal;
